import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box, Button, Chip, Typography } from "@mui/material";
import { useParams } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import type { SuggestedActionsActions } from "./suggestedActionsExtension";
import {
  MAX_SUGGESTIONS,
  SUGGESTION_TYPE_LABELS,
  SUGGESTION_TYPES,
  createSuggestedAction,
  validateSuggestions,
} from "./suggestedActionsSchema";
import type { SuggestionRowErrors } from "./suggestedActionsSchema";
import { SuggestionRow } from "./SuggestionRow";
import { SectionAccordion } from "../../compose/SectionAccordion";
import type { ExtensionRenderProps } from "../../editor/extensionContract";
import { useMessageDraftStore } from "../../editor/messageDraftStoreFactory";
import { isTextComposerRcsRichEnabledForSite } from "../../flag";

import {
  AddIcon,
  CheckCircleIcon as CheckCircle,
  DragIndicatorIcon,
  SmsOutlinedIcon,
} from "@/icons";
import { useDynamicFlag } from "@/utils/featureFlagOverride";

export function SuggestedActions({
  authoringState,
  disabled = false,
  activeChannel,
}: ExtensionRenderProps) {
  const { siteId } = useParams({ strict: false });
  const rcsRichEnabled = useDynamicFlag(
    "VITE_FF_TEXT_COMPOSER_RCS_RICH",
    isTextComposerRcsRichEnabledForSite(siteId),
  );
  const [expanded, setExpanded] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const {
    addSuggestion,
    setSuggestion,
    duplicateSuggestion,
    moveSuggestion,
    removeSuggestion,
    clearSuggestions,
  } = useMessageDraftStore((s) => s.actions as SuggestedActionsActions);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const suggestions = useMemo(
    () => authoringState.suggestions ?? [],
    [authoringState.suggestions],
  );
  const errors = useMemo(() => validateSuggestions(suggestions), [suggestions]);

  // A blank row is invalid the moment it's added, but nagging before the
  // marketer has touched anything is noise. Reveal a field's error once it has
  // been blurred; the send button stays gated regardless via the extension's
  // `validate` (selectIsAuthoringValid). Keyed by stable row id so the touched
  // state survives reordering.
  const [touched, setTouched] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const markTouched = useCallback(
    (rowId: string, field: keyof SuggestionRowErrors) => {
      setTouched((prev) => {
        const key = `${rowId}.${field}`;
        if (prev.has(key)) return prev;
        return new Set(prev).add(key);
      });
    },
    [],
  );

  // Stable per-row keys for dnd-kit and React, sourced from the row's own id
  // (minted in createSuggestedAction). The id travels with the row across
  // add/remove/reorder, so nothing needs to be permuted by hand. The index
  // fallback is only for hand-built rows that never carry an id.
  const ids = useMemo(
    () =>
      suggestions.map((suggestion, index) => suggestion.id ?? String(index)),
    [suggestions],
  );

  if (!rcsRichEnabled) return null;

  const atMax = suggestions.length >= MAX_SUGGESTIONS;
  const actionsUnsupported = activeChannel != null && activeChannel !== "RCS";

  return (
    <SectionAccordion
      title="Suggested Actions"
      icon={SmsOutlinedIcon}
      chips={
        actionsUnsupported ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Not supported on ${activeChannel}`}
          />
        ) : suggestions.length > 0 ? (
          <CheckCircle color="primary" />
        ) : undefined
      }
      summaryId="text-composer-suggested-actions-header"
      detailsId="text-composer-suggested-actions-content"
      expanded={actionsUnsupported ? false : expanded}
      disabled={disabled || actionsUnsupported}
      onExpandedChange={setExpanded}
    >
      <Typography variant="body3" color="text.secondary">
        {`Provide up to ${MAX_SUGGESTIONS} suggestions for quick replies or actions.`}
      </Typography>

      {suggestions.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={({ active }) => setActiveId(String(active.id))}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={({ active, over }) => {
            setActiveId(null);
            if (!over || active.id === over.id) return;
            const from = ids.indexOf(String(active.id));
            const to = ids.indexOf(String(over.id));
            if (from === -1 || to === -1) return;
            moveSuggestion(from, to);
          }}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {suggestions.map((suggestion, index) => {
                const rowId = ids[index];
                const rowErrors = errors[index];
                const visibleErrors = rowErrors
                  ? (Object.fromEntries(
                      Object.entries(rowErrors).filter(([field]) =>
                        touched.has(`${rowId}.${field}`),
                      ),
                    ) as SuggestionRowErrors)
                  : undefined;
                return (
                  <SuggestionRow
                    key={rowId}
                    id={rowId}
                    position={index + 1}
                    suggestion={suggestion}
                    errors={visibleErrors}
                    disabled={disabled}
                    onChange={(next) => setSuggestion(index, next)}
                    onFieldBlur={(field) => markTouched(rowId, field)}
                    onDuplicate={() => duplicateSuggestion(index)}
                    onRemove={() => removeSuggestion(index)}
                  />
                );
              })}
            </Box>
          </SortableContext>
          <DragOverlay>
            {(() => {
              const activeIndex = activeId ? ids.indexOf(activeId) : -1;
              const active = suggestions[activeIndex];
              if (!active) return null;
              return (
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: `${theme.shape.borderRadiusSm}px`,
                    boxShadow: theme.shadows[8],
                    cursor: "grabbing",
                  })}
                >
                  <DragIndicatorIcon
                    sx={{ fontSize: 16, color: "action.active" }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {`Suggestion ${activeIndex + 1} – ${SUGGESTION_TYPE_LABELS[active.type]}`}
                  </Typography>
                </Box>
              );
            })()}
          </DragOverlay>
        </DndContext>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() =>
            addSuggestion(createSuggestedAction(SUGGESTION_TYPES[0]))
          }
          disabled={disabled || atMax}
        >
          Add suggestion
        </Button>
        {suggestions.length > 0 && (
          <Button
            size="small"
            color="inherit"
            onClick={clearSuggestions}
            disabled={disabled}
            sx={{ color: "text.secondary", textTransform: "none" }}
          >
            Clear all
          </Button>
        )}
      </Box>
    </SectionAccordion>
  );
}
